import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';
import { JsonSchema } from '../models/json-schema.types';

export type ConfigurationFieldType = 
  | 'ENUM' 
  | 'BIG_STRING' 
  | 'JSON' 
  | 'BOOLEAN' 
  | 'NUMBER' 
  | 'STRING' 
  | 'AGENT_SELECTOR'
  | 'ACTIVITY_SELECTOR'
  | 'SECRET_SELECTOR';

export type RuleParameterType = 'string' | 'list' | 'boolean';

export interface RuleTypeParameter {
  Name: string;
  Type: RuleParameterType;
  Required: boolean;
  Default: unknown | null;
  Description: string;
}

export interface RuleTypeDefinition {
  RuleType: string;
  Description: string;
  Parameters: RuleTypeParameter[];
}

export interface ConfigurationField {
  Alias: string;
  Description: string;
  Id: string;
  Type: ConfigurationFieldType;
  IsRequired: boolean;
  DynamicDataEnabled: boolean;
  /** For JSON fields this is a JSON *string*. */
  DefaultValue: string | null;
  PossibleValues: string[] | null;
  /** Only set for `JSON` fields; drives the structured (schema-based) editor. */
  JsonSchema?: JsonSchema | null;
}

export interface FlowStep {
  Alias: string;
  Type: string;
  Address: string;
  Description: string;
  ConfigurationFields: ConfigurationField[];
  Deprecated: boolean;
  Disabled: boolean;
  OutputSchema: JsonSchema | null;
}

export interface FlowModule {
  Id: string;
  Alias: string;
  Steps: FlowStep[];
}

export interface FlowSystemModulesResponse {
  Modules: FlowModule[];
}


@Injectable({
  providedIn: 'root'
})
export class FlowSystemService {
  private api: AxiosInstance;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private authService: KeycloakAuthService, private apiConfig: ApiConfigService) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getAvailableFlowSteps(): Promise<FlowSystemModulesResponse | null> {
    try {
      const response = await this.api.get("/flowsystem/steps");
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getRuleValidatorRuleTypes(): Promise<RuleTypeDefinition[]> {
    try {
      const response = await this.api.get("/flowsystem/steps/utilities/rule-validator/rule-types");
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
